/**
 * Graphics settings (pure): defaults, validation, quality profiles and localStorage persistence.
 * Storage is injected so tests can use a fake; every read/write is wrapped in try/catch
 * (private mode, blocked storage, quota errors must never break the game).
 */

export type RendererKind = 'toon' | 'placeholder';
export type QualityPreset = 'low' | 'medium' | 'high';

export interface GraphicsSettings {
  renderer: RendererKind;
  /** Floor albedo multiplier (0.4..1.6). Keep the floor dark so telegraphs read. */
  floorBrightness: number;
  /** Telegraph / spell effect intensity multiplier (0.2..1.5). */
  fxIntensity: number;
  outlines: boolean;
  bloom: boolean;
  /** Light camera shake on big crits and nearby enemy slams (scaled by fxIntensity). */
  screenShake: boolean;
  /** Hue (degrees) of the cool shadow band. */
  shadowHue: number;
  quality: QualityPreset;
  /** Drop to a lower preset while frames stay over budget (never above `quality`). */
  autoQuality: boolean;
  /** +atmos: outdoor time of day: a slow real-time day / night cycle, or always day. */
  timeOfDay: TimeOfDayMode;
}

export type TimeOfDayMode = 'cycle' | 'day';
export const TIME_OF_DAY_MODES: readonly TimeOfDayMode[] = ['cycle', 'day'];

export const DEFAULT_SETTINGS: Readonly<GraphicsSettings> = Object.freeze({
  renderer: 'toon',
  floorBrightness: 1,
  fxIntensity: 1,
  outlines: true,
  bloom: true,
  screenShake: true,
  shadowHue: 236,
  quality: 'high',
  autoQuality: true,
  timeOfDay: 'cycle',
});

export const SETTINGS_KEY = 'keystone.graphics.v1';

export const RENDERER_KINDS: readonly RendererKind[] = ['toon', 'placeholder'];
export const QUALITY_PRESETS: readonly QualityPreset[] = ['low', 'medium', 'high'];

export const SETTING_RANGES = {
  floorBrightness: { min: 0.4, max: 1.6, step: 0.05 },
  fxIntensity: { min: 0.2, max: 1.5, step: 0.05 },
  shadowHue: { min: 0, max: 360, step: 1 },
} as const;

export interface QualityProfile {
  pixelRatioCap: number;
  /** MSAA samples on the post-processing target. */
  msaa: number;
  bloomAllowed: boolean;
  /** Inverted-hull outlines on static props/walls (units always keep theirs when outlines are on). */
  propOutlines: boolean;
  shadows: boolean;
  shadowMapSize: number;
  /** Mobs cast real shadows (heroes always do when shadows are on). */
  mobShadows: boolean;
  /** VRM spring-bone (hair, cloth) simulation rate cap in Hz; 0 = off. */
  springHz: number;
}

export function qualityProfile(q: QualityPreset): QualityProfile {
  switch (q) {
    case 'low':
      return { pixelRatioCap: 1, msaa: 0, bloomAllowed: false, propOutlines: false, shadows: false, shadowMapSize: 1024, mobShadows: false, springHz: 30 };
    case 'medium':
      return { pixelRatioCap: 1.5, msaa: 2, bloomAllowed: true, propOutlines: true, shadows: true, shadowMapSize: 1024, mobShadows: false, springHz: 30 };
    case 'high':
      return { pixelRatioCap: 2, msaa: 4, bloomAllowed: true, propOutlines: true, shadows: true, shadowMapSize: 2048, mobShadows: true, springHz: 60 };
  }
}

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

/** Coerce anything (parsed JSON, partial objects) into valid settings. */
export function sanitizeSettings(raw: unknown): GraphicsSettings {
  const s: GraphicsSettings = { ...DEFAULT_SETTINGS };
  if (!raw || typeof raw !== 'object') return s;
  const r = raw as Record<string, unknown>;
  if (typeof r['renderer'] === 'string' && (RENDERER_KINDS as readonly string[]).includes(r['renderer'])) s.renderer = r['renderer'] as RendererKind;
  if (typeof r['quality'] === 'string' && (QUALITY_PRESETS as readonly string[]).includes(r['quality'])) s.quality = r['quality'] as QualityPreset;
  if (typeof r['outlines'] === 'boolean') s.outlines = r['outlines'];
  if (typeof r['bloom'] === 'boolean') s.bloom = r['bloom'];
  if (typeof r['screenShake'] === 'boolean') s.screenShake = r['screenShake'];
  if (typeof r['autoQuality'] === 'boolean') s.autoQuality = r['autoQuality'];
  if (typeof r['timeOfDay'] === 'string' && (TIME_OF_DAY_MODES as readonly string[]).includes(r['timeOfDay'])) s.timeOfDay = r['timeOfDay'] as TimeOfDayMode;
  const num = (k: 'floorBrightness' | 'fxIntensity' | 'shadowHue'): void => {
    const v = r[k];
    if (typeof v === 'number' && Number.isFinite(v)) s[k] = clamp(v, SETTING_RANGES[k].min, SETTING_RANGES[k].max);
  };
  num('floorBrightness');
  num('fxIntensity');
  num('shadowHue');
  return s;
}

type ReadStore = Pick<Storage, 'getItem'>;
type WriteStore = Pick<Storage, 'setItem'>;

function defaultStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export function loadSettings(storage: ReadStore | null = defaultStorage()): GraphicsSettings {
  try {
    const raw = storage?.getItem(SETTINGS_KEY);
    return sanitizeSettings(raw ? JSON.parse(raw) : null);
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

/** Returns false when the write failed (storage blocked or full). */
export function saveSettings(s: GraphicsSettings, storage: WriteStore | null = defaultStorage()): boolean {
  try {
    if (!storage) return false;
    storage.setItem(SETTINGS_KEY, JSON.stringify(sanitizeSettings(s)));
    return true;
  } catch {
    return false;
  }
}

/** `?renderer=toon|placeholder` override (not persisted). */
export function rendererFromParam(v: string | null): RendererKind | null {
  return v && (RENDERER_KINDS as readonly string[]).includes(v) ? (v as RendererKind) : null;
}
